import { BehaviorSubject, combineLatest, fromEvent, Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith, take } from 'rxjs/operators';
import { isNullOrWhitespace } from 'src/util/string';
import { getOrElse } from 'src/util/values';

export interface TcsAudioPlayerOptions {
  loop?: boolean;
  load?: boolean;
  volume?: number;
}

class WindowInteractionService {
  private readonly _clicked$ = new BehaviorSubject<boolean>(false);

  public get clicked$() {
    return this._clicked$.asObservable().pipe(distinctUntilChanged());
  }

  constructor() {
    const isInteractionRequired = this.isUserInteractionRequired();

    if (!isInteractionRequired) {
      this._clicked$.next(true);
    } else {
      fromEvent(window, 'click')
        .pipe(
          map(() => true),
          startWith(false),
          take(2),
        )
        .subscribe((v) => this._clicked$.next(v));
    }
  }

  isUserInteractionRequired() {
    try {
      const audioCtx = new (window.AudioContext ?? (window as any).webkitAudioContext)();
      return audioCtx.state === 'suspended';
    } catch {
      return true;
    }
  }
}
const windowInteractionService = new WindowInteractionService();

export class TcsAudioPlayer {
  private readonly _player = new Audio();
  private readonly _loaded$ = new BehaviorSubject(false);
  private readonly _playing$ = new BehaviorSubject(false);
  private readonly _volume$ = new BehaviorSubject(1);
  private readonly _muted$ = new BehaviorSubject(false);
  private readonly _position$ = new BehaviorSubject(0);
  private readonly _error$ = new BehaviorSubject<MediaError | null>(null);

  public readonly audioLoaded$: Observable<boolean>;
  public readonly ready$: Observable<boolean>;
  public readonly playing$: Observable<boolean>;
  public readonly volume$: Observable<number>;
  public readonly muted$: Observable<boolean>;
  public readonly position$: Observable<number>;
  public readonly error$: Observable<MediaError | null>;

  get volume() {
    return this._player.volume;
  }
  set volume(value: number) {
    this._player.volume = value > 1 ? 1 : value < 0 ? 0 : value;
    this._volume$.next(this._player.volume);
  }

  get muted() {
    return this._player.muted;
  }
  set muted(value: boolean) {
    this._player.muted = value;
    this._muted$.next(value);
  }

  get length(): number {
    return this._player.seekable.end(0);
  }

  constructor(public readonly src: string, options: TcsAudioPlayerOptions = {}) {
    if (isNullOrWhitespace(src)) {
      throw new Error('Audio player source expected');
    }
    this._player.src = src;
    this._player.loop = getOrElse(options.loop, false);
    this._player.volume = getOrElse(options.volume, 1);
    this._player.onplaying = () => this._playing$.next(true);
    this._player.onended = () => this._playing$.next(false);
    this._player.onpause = () => this._playing$.next(false);
    this._player.onloadedmetadata = () => this._loaded$.next(true);
    this._player.ontimeupdate = () => this._position$.next(this._player.currentTime);
    this._player.onerror = () => this._error$.next(this._player.error);

    const windowClicked$ = windowInteractionService.clicked$;
    this.audioLoaded$ = this._loaded$.asObservable().pipe(distinctUntilChanged());
    this.ready$ = combineLatest([this._loaded$, windowClicked$]).pipe(
      map(([loaded, clicked]) => loaded && clicked),
      distinctUntilChanged(),
    );
    this.playing$ = this._playing$.asObservable().pipe(distinctUntilChanged());
    this.volume$ = this._volume$.asObservable().pipe(distinctUntilChanged());
    this.muted$ = this._muted$.asObservable().pipe(distinctUntilChanged());
    this.position$ = this._position$.asObservable().pipe(distinctUntilChanged());
    this.error$ = this._error$.asObservable().pipe(distinctUntilChanged());

    if (options.load != null && options.load) {
      this._player.load();
    }
  }

  public play() {
    try {
      this._player.play();
    } catch {}
  }

  public pause() {
    this._player.pause();
  }

  public stop() {
    if (this._playing$.value) {
      this._player.currentTime = 0;
      this._player.pause();
    }
  }

  public load() {
    this._player.load();
  }
}
